import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from 'flowbite-react';
import { Change, User } from '../../models';
import { loadUsers, useAppDispatch, useAppSelector } from '../../redux';
import { MultiSelect, Select } from '..';
import type { Value } from '../Select';
import type { SearchFilters } from '../../services/Search';

export const SEARCH_STATUSES = [
    Change.STATUS_PENDING_TRIAGE,
    Change.STATUS_PENDING_ESTIMATE,
    Change.STATUS_ESTIMATED,
    Change.STATUS_PENDING_PLANIFICATION,
    Change.STATUS_PLANNED,
    Change.STATUS_ASSIGNED,
    Change.STATUS_IN_PROGRESS,
    Change.STATUS_TEAM_REVIEW,
    Change.STATUS_CLIENT_REVIEW,
    Change.STATUS_PENDING_DEPLOYMENT,
    Change.STATUS_DEPLOYED,
    Change.STATUS_FINISHED,
];

interface SearchAdvancedFiltersProps {
    filters: SearchFilters;
    onChange: (filters: SearchFilters) => void;
    /** When false, hide assignee + status (owned by the parent toolbar). Default true. */
    showCoreFilters?: boolean;
}

const SearchAdvancedFilters: React.FC<SearchAdvancedFiltersProps> = ({
    filters,
    onChange,
    showCoreFilters = true,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const users = useAppSelector((state) => state.users.users) || [];

    useEffect(() => {
        if (!showCoreFilters) return;
        if (!users.length) {
            dispatch(loadUsers());
        }
    }, [dispatch, users.length, showCoreFilters]);

    const selectableAssignees = useMemo(() => {
        return [...users].sort((a: User, b: User) => a.fullname().localeCompare(b.fullname()));
    }, [users]);

    const assigneeValue: Value = filters.unassigned
        ? 'unassigned'
        : (filters.assignee || 'all');

    const update = (patch: Partial<SearchFilters>) => {
        onChange({ ...filters, ...patch });
    };

    const onAssigneeChange = (value: Value) => {
        if (value === 'all') {
            update({ assignee: undefined, unassigned: false });
            return;
        }
        if (value === 'unassigned') {
            update({ assignee: undefined, unassigned: true });
            return;
        }
        update({ assignee: String(value), unassigned: false });
    };

    return (
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            {showCoreFilters && (
                <>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                            {t('search.filters.assignee')}
                        </label>
                        <Select
                            defaultValue={assigneeValue}
                            onChange={onAssigneeChange}
                            placeholder={t('search.filters.assignee') || ''}
                            options={[
                                { value: 'all', label: t('search.filters.all_assignees') || '' },
                                { value: 'unassigned', label: t('search.filters.unassigned') || '' },
                                ...selectableAssignees.map((user: User) => ({
                                    value: user._id,
                                    label: user.fullname() || user.email,
                                })),
                            ]}
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                            {t('search.filters.status')}
                        </label>
                        <MultiSelect
                            placeholder={t('search.filters.all_statuses') || ''}
                            defaultValue={filters.statuses || []}
                            onChange={(values) => update({ statuses: values.map(String) })}
                            options={SEARCH_STATUSES.map((status) => ({
                                value: status,
                                label: t(`changes.progress_status_type.${status}`) || status,
                            }))}
                        />
                    </div>
                </>
            )}

            <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    {t('search.filters.title')}
                </label>
                <TextInput
                    type="text"
                    value={filters.title || ''}
                    onChange={(e) => update({ title: e.target.value })}
                    placeholder={t('search.filters.title_placeholder') || ''}
                />
            </div>

            <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    {t('search.filters.ticket_number')}
                </label>
                <TextInput
                    type="text"
                    value={filters.ticketNumber || ''}
                    onChange={(e) => update({ ticketNumber: e.target.value })}
                    placeholder={t('search.filters.ticket_number_placeholder') || ''}
                />
            </div>

            <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    {t('search.filters.description')}
                </label>
                <TextInput
                    type="text"
                    value={filters.description || ''}
                    onChange={(e) => update({ description: e.target.value })}
                    placeholder={t('search.filters.description_placeholder') || ''}
                />
            </div>

            <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    {t('search.filters.requestor_email')}
                </label>
                <TextInput
                    type="text"
                    value={filters.requestorEmail || ''}
                    onChange={(e) => update({ requestorEmail: e.target.value })}
                    placeholder={t('search.filters.requestor_email_placeholder') || ''}
                />
            </div>

            <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    {t('search.filters.created_after')}
                </label>
                <input
                    type="date"
                    value={filters.createdAfter || ''}
                    onChange={(e) => update({ createdAfter: e.target.value || undefined })}
                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-2.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500"
                />
            </div>

            <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    {t('search.filters.created_before')}
                </label>
                <input
                    type="date"
                    value={filters.createdBefore || ''}
                    onChange={(e) => update({ createdBefore: e.target.value || undefined })}
                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-2.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500"
                />
            </div>

            <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    {t('search.filters.updated_after')}
                </label>
                <input
                    type="date"
                    value={filters.updatedAfter || ''}
                    onChange={(e) => update({ updatedAfter: e.target.value || undefined })}
                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-2.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500"
                />
            </div>

            <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    {t('search.filters.updated_before')}
                </label>
                <input
                    type="date"
                    value={filters.updatedBefore || ''}
                    onChange={(e) => update({ updatedBefore: e.target.value || undefined })}
                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-2.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500"
                />
            </div>
        </div>
    );
};

export default SearchAdvancedFilters;
